import React, { useState, useEffect, useMemo, useRef } from 'react'

import 'react-grid-layout/css/styles.css';

import { cloneDeep } from 'lodash';
import RGL, { WidthProvider } from 'react-grid-layout';
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";

import NetworkStatus from "./gridComponents/NetworkStatus.js"
import UsagePieChart from './gridComponents/UsagePieChart.js';
import UsageRankingList from "./gridComponents/UsageRankingList.js"
import UsageRankingBarChart from "./gridComponents/UsageRankingBarChart.js"
import UsageLineChart from "./gridComponents/UsageLineChart.js"
import ApChannelUtilization from './gridComponents/ApChannelUtilization.js';
import PoEPowerConsumption from "./gridComponents/PoEPowerConsumption.js";
import UsageBarChart from "./gridComponents/UsageBarChart.js";

import icn_bar_chart_horizontal from 'assets/images/icons/btn_wireless.svg';
import icn_line_traffic from 'assets/images/icons/icn_line_traffic.svg';

import { defaultWidgetCard, networkWidgetIds } from "../const";
import { getRankingListData } from "../helper.js"
import { getPieChartData, getLineChartData, getBarChartData } from "utils/getEChartData";
import { bytesToKB } from "utils/format.js";

const ReactGridLayout = WidthProvider(RGL);
const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const GridLayout = (props) => {
  const { statsData, fetchDataMode, setFetchDataMode,
    targetWidget, widgetSet, setWidgetSet,
    actionStatus, setShowMenu, openDeviceDrawer
  } = props;

  const [widgetUuid, _setWidgetUuid] = useState(uuidv4());
  const [updateUuid, _setUpdateUuid] = useState(uuidv4());

  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const rawBlocksRef = useRef([]);

  const [allowDrag, setAllowDrag] = useState(false);
  const [sortable, setSortable] = useState(false);
  const [originalLayout, setOriginalLayout] = useState(cloneDeep(defaultWidgetCard));
  const [layout, setLayout] = useState([]);
  const [gridKey, setGridKey] = useState(0);
  const [ifChange, setIfChange] = useState(false);
  const [hasFirstRender, setHasFirstRender] = useState(false);

  const [accessPointsOverviewClientUsageData, setAccessPointsOverviewClientUsageData] = useState([]);
  const [accessPointsOverviewTrafficUsageData, setAccessPointsOverviewTrafficUsageData] = useState([]);
  const [wirelessClientsRssiUsageData, setWirelessClientsRssiUsageData] = useState([]);
  const [top5WirelessClientsbyTrafficUsageData, setTop5WirelessClientsbyTrafficUsageData] = useState([]);
  const [top5SwitchesByTrafficUsageData, setTop5SwitchesByTrafficUsageData] = useState([]);
  const [top5AccessPointsByTrafficUsageData, setTop5AccessPointsByTrafficUsageData] = useState([]);
  const [wirelessClientsBandUsageData, setWirelessClientsBandUsageData] = useState([]);
  const [wirelessTrafficUsageData, setWirelessTrafficUsageData] = useState([]);
  const [currentWirelessTrafficUsageData, setCurrentWirelessTrafficUsageData] = useState({})
  const [top5SsidByTrafficUsageData, setTop5SsidByTrafficUsageData] = useState([]);
  const [top5AccessPointsByWirelessClientUsageData, setTop5AccessPointsByWirelessClientUsageData] = useState([]);
  const [wirelessClientsActivitiesUsageData, setWirelessClientsActivitiesUsageData] = useState([]);

  const accessPointsOverviewData = useMemo(() => [
    {
      key: "accessPointsOverviewClient",
      label: "Clients",
      data: accessPointsOverviewClientUsageData,
    },
    {
      key: "accessPointsOverviewTraffic",
      label: "Traffic",
      data: accessPointsOverviewTrafficUsageData,
    },
  ], [accessPointsOverviewClientUsageData, accessPointsOverviewTrafficUsageData]);

  const rssiChartData = useMemo(() => [
    {
      key: "rssi",
      label: "Clients",
      data: wirelessClientsRssiUsageData,
    },
  ], [wirelessClientsRssiUsageData]);

  const rssiChartLegend = useMemo(() => {
    return wirelessClientsRssiUsageData.reduce((acc, item, index, array) => {
      acc[item.name] = item.value;
      if (index === array.length - 1) {
        acc.key = "rssi";
        acc.unit = "Client(s)";
      }
      return acc;
    }, {});
  }, [wirelessClientsRssiUsageData]);

  const bandChartData = useMemo(() => [
    {
      key: "wirelessClientsBand",
      label: "Clients",
      data: wirelessClientsBandUsageData,
    },
  ], [wirelessClientsBandUsageData]);

  const bandChartLegend = useMemo(() => {
    return wirelessClientsBandUsageData.reduce((acc, item, index, array) => {
      acc[item.name] = item.value;
      if (index === array.length - 1) {
        acc.unit = "Client(s)";
      }
      return acc;
    }, {});
  }, [wirelessClientsBandUsageData]);

  const getWidgetData = async () => {
    let tmpResource = 'users/dashboard.read';
    sendRequest(tmpResource, widgetUuid);
  };

  const setWidgetData = async (updateList) => {
    if (checkMockup === 'true') {
      return;
    }
    let tmpResource = 'users/dashboard.update';
    let tmpParam = { blocks: updateList };
    sendRequest(tmpResource, updateUuid, null, { param: tmpParam });
  };

  const checkTargetXY = (target) => {
    let newItem = cloneDeep(target);
    let list = cloneDeep(layout)
    // const xColumn = 3

    if (list.length === 0) {
      newItem.x = 0;
      newItem.y = 0;
    } else {
      let prevItem = list[list.length - 1]

      let checkList = cloneDeep(layout);
      let columnAry = [];
      let currentSubAry = [checkList[0]]
      for (let i = 1; i < checkList.length; i++) {
        if (checkList[i].x > checkList[i - 1].x) {
          currentSubAry.push(checkList[i]);
        } else {
          columnAry.push(currentSubAry);
          currentSubAry = [checkList[i]];
        }
      }
      columnAry.push(currentSubAry);

      let lofColumnAry = columnAry.length;
      let prevColumn = columnAry[lofColumnAry - 1]

      let prevColumnWidth = 0
      let columnHeight = prevColumn[0].h, ifSameHeight = true;
      for (const item of prevColumn) {
        prevColumnWidth += item.w;
        if (item.h !== columnHeight) {
          ifSameHeight = false;
        }
      }

      let prevMaxYItem = prevColumn[0]
      if (!ifSameHeight) {
        prevMaxYItem = prevColumn.reduce((max, current) => {
          return (current.y > max.y) ? current : max;
        });
      }

      if (newItem.w === 3) {
        newItem.x = 0;
        newItem.y = prevMaxYItem.y + prevMaxYItem.h;
      } else if (newItem.w === 2) {
        if (prevColumnWidth > 1) {
          newItem.x = 0;
          if (prevColumnWidth > 2 || prevItem.w === 2) {
            newItem.y = prevMaxYItem.y + prevMaxYItem.h;
          } else {
            if (ifSameHeight) {
              newItem.y = prevItem.y + prevItem.h;
            } else {
              if (prevMaxYItem.x === 2) {
                newItem.y = prevColumn[0].y + prevColumn[0].h;
              } else {
                newItem.y = prevMaxYItem.y + prevMaxYItem.h;
              }
            }
          }
        } else {
          if (prevItem.x === 1) {
            newItem.x = 0;
            newItem.y = prevItem.y + prevItem.h;
          } else {

            if (prevItem.x === 0) {
              newItem.x = 1;
            } else {
              newItem.x = 0;
            }
            if (lofColumnAry <= 1) {
              newItem.y = 0;
            } else {
              let range = { start: newItem.x, end: newItem.x + newItem.w };
              let reverseList = cloneDeep(columnAry)
              reverseList = reverseList.reverse();
              let finalY = 0;

              for (let i = 1; i < reverseList.length; i++) {
                if (finalY !== 0) {
                  break;
                }
                // let needLoop = true;
                let prevColumn2 = reverseList[i];
                let prevColumnWidth2 = 0, prevItem2 = {};
                let columnHeight2 = prevColumn2[0].h, ifSameHeight2 = true;
                for (const item of prevColumn2) {
                  prevItem2 = item;
                  prevColumnWidth2 += item.w;
                  if (item.h !== columnHeight2) {
                    ifSameHeight2 = false;
                  }
                }
                let prevMaxYItem2 = prevColumn2[0]
                if (!ifSameHeight2) {
                  prevMaxYItem2 = prevColumn2.reduce((max, current) => {
                    return (current.y > max.y) ? current : max;
                  });
                }

                if (prevItem2.w > 2) {
                  finalY = prevItem2.y + prevItem2.h;
                } else if (prevColumnWidth2 === 2 && prevColumnWidth2 === 2) {
                  finalY = prevItem2.y + prevItem2.h;
                } else if (prevColumnWidth2 >= 2) {
                  if (prevMaxYItem2.w > 1 || ifSameHeight2) {
                    finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                  } else {
                    if (range.start === 0) {
                      if (prevMaxYItem2.x < 2) {
                        finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                      } else {
                        finalY = prevColumn2[0].y + prevColumn2[0].h;
                      }
                    } else {
                      if (prevMaxYItem2.x > 0) {
                        finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                      } else {
                        finalY = prevColumn2[0].y + prevColumn2[0].h;
                      }
                    }
                  }
                } else {
                  if (range.start === 0 && prevMaxYItem2.x < 2) {
                    finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                  } else if (range.start === 1 && prevMaxYItem2.x > 0) {
                    finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                  } else {
                  }
                }
              }
              newItem.y = finalY;
            }
          }
        }
      } else {
        if (prevColumnWidth > 2) {
          newItem.x = 0;
          if (ifSameHeight) {
            newItem.y = prevItem.y + prevItem.h;
          } else {
            if (prevMaxYItem.x === 0) {
              newItem.y = prevMaxYItem.y + prevMaxYItem.h;
            } else {
              newItem.y = prevColumn[0].y + prevColumn[0].h;
            }
          }
        } else {
          if (prevItem.w === 2) {
            if (prevItem.x === 0) {
              newItem.x = 2;
            } else {
              newItem.x = 0;
            }
          } else {
            if (prevColumnWidth === 1) {
              if (prevItem.x === 0) {
                newItem.x = 1;
              } else {
                newItem.x = 0;
              }
            } else {
              let card1 = prevColumn[0], card2 = prevColumn[1]
              if (card1.x === 1) {
                newItem.x = 0;
              } else if (card2.x === 1) {
                newItem.x = 2;
              } else {
                newItem.x = 1;
              }
            }
          }

          if (lofColumnAry <= 1) {
            newItem.y = 0;
          } else {
            let range = { start: newItem.x, end: newItem.x + newItem.w };
            // let findPositon = false;
            let reverseList = cloneDeep(columnAry)
            reverseList = reverseList.reverse();
            let finalY = 0;

            for (let i = 1; i < reverseList.length; i++) {
              if (finalY !== 0) {
                break;
              }

              // let needLoop = true;
              let prevColumn2 = reverseList[i];
              let prevColumnWidth2 = 0, prevItem2 = {};
              let columnHeight2 = prevColumn2[0].h, ifSameHeight2 = true;
              for (const item of prevColumn2) {
                prevItem2 = item;
                prevColumnWidth2 += item.w;
                if (item.h !== columnHeight2) {
                  ifSameHeight2 = false;
                }
              }
              let prevMaxYItem2 = prevColumn2[0]
              if (!ifSameHeight2) {
                prevMaxYItem2 = prevColumn2.reduce((max, current) => {
                  return (current.y > max.y) ? current : max;
                });
              }

              if (prevItem2.w > 2) {
                finalY = prevItem2.y + prevItem2.h;
              } else if (prevColumnWidth2 === 2 && prevMaxYItem2.w === 2) {
                if (prevItem2.x === 0) {
                  if (range.start < 2) {
                    finalY = prevItem2.y + prevItem2.h;
                  }
                  else {
                  }
                } else {
                  if (range.start > 0) {
                    finalY = prevItem2.y + prevItem2.h;
                  }
                  else {
                  }
                }
              } else if (prevColumnWidth2 > 2) {
                if (ifSameHeight2) {
                  finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                } else {
                  if (prevMaxYItem2.w === 1 && prevMaxYItem2.x === range.start) {
                    finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                  } else if (prevMaxYItem2.w === 2) {
                    finalY = prevColumn2[0].y + prevColumn2[0].h;
                    if (prevMaxYItem2.x === 0 && range.start === 2) {
                      finalY = prevItem2.y + prevItem2.h;
                    } else if (prevMaxYItem2.x === 1 && range.start > 0) {
                      finalY = prevMaxYItem2.y + prevMaxYItem2.h;
                    }
                  } else {
                    finalY = prevItem2.y + prevItem2.h;
                    if (prevItem2.x === prevMaxYItem2.x) {
                      finalY = prevColumn2[0].y + prevColumn2[0].h;
                    }
                  }
                }
              } else if (prevColumnWidth2 === 2) {
                let card1 = prevColumn2[0], card2 = prevColumn2[1]
                if (card1.x === range.start) {
                  finalY = prevColumn2[0].y + prevColumn2[0].h;
                } else if (card2.x === range.start) {
                  finalY = prevColumn2[1].y + prevColumn2[1].h;
                } else {
                }
              } else {
                if (prevItem2.x === range.start) {
                  finalY = prevItem2.y + prevItem2.h;
                } else {
                }
              }
            }
            newItem.y = finalY;
          }
        }
      }
    }
    list.push(newItem)
    let sortList = cloneDeep(list)
    return sortList;
  }

  const addItem = (id) => {
    let defaultCard = cloneDeep(defaultWidgetCard)
    let target = defaultCard.filter(item => item.i === id)[0]
    target.isBounded = undefined;
    target.isDraggable = undefined;
    target.isResizable = undefined;
    target.maxH = undefined;
    target.maxW = undefined;
    target.minH = undefined;
    target.minW = undefined;
    target.resizeHandles = undefined;
    target.moved = undefined;
    let newLayout = checkTargetXY(target)
    setIfChange(true)
    setLayout(newLayout);
  }

  const removeItem = (id) => {
    setIfChange(true)
    setLayout(layout.filter(item => item.i !== id));
  };

  const makeLayoutData = (list) => {
    let defaultLayout = cloneDeep(originalLayout);
    let systemLayout = cloneDeep(list).filter(item => networkWidgetIds.includes(item.blockId));
    let formatLayout = []

    systemLayout.forEach((subSystem, i) => {
      let tmpObj = {
        i: subSystem.blockId,
        w: '',
        h: '',
        x: subSystem.x,
        y: subSystem.y,
        isBounded: undefined,
        isDraggable: undefined,
        isResizable: undefined,
        maxH: undefined,
        maxW: undefined,
        minH: undefined,
        minW: undefined,
        resizeHandles: undefined,
        moved: undefined,
      }

      defaultLayout.forEach((subDefault, i) => {
        if (tmpObj.i === subDefault.i) {
          tmpObj.w = subDefault.w;
          tmpObj.h = subDefault.h;
        }
      })
      formatLayout.push(tmpObj)
    })
    return formatLayout;
  }

  const checkMenuSetting = (list) => {
    let selectedMenu = cloneDeep(widgetSet);
    list.forEach((subSystem, i) => {
      for (let key in widgetSet) {
        if (widgetSet[key].id === subSystem.i) {
          selectedMenu[key].check = true;
          break;
        }
      }
    })
    return selectedMenu;
  }

  const updateLayout = async () => {
    let updateList = cloneDeep(layout)
    
    // Filter out Network widgets to keep Surveillance widgets
    const otherDashboardBlocks = rawBlocksRef.current.filter(
      (block) => !networkWidgetIds.includes(block.blockId)
    );

    let currentDashboardBlocks = []
    updateList.forEach((item, index) => {
      let obj = {
        blockId: item.i,
        x: item.x,
        y: item.y,
        visible: true,
      }
      currentDashboardBlocks.push(obj)
    })

    const resultList = [...otherDashboardBlocks, ...currentDashboardBlocks];

    setWidgetData(resultList)
    setOriginalLayout(updateList);
    setGridKey(prevKey => prevKey + 1);
    rawBlocksRef.current = resultList;
  };

  const initLayout = async () => {
    let originData = cloneDeep(defaultWidgetCard);
    let formatLayout = [];
    setIfChange(true);

    originData.forEach((item, i) => {
      let tmpObj = {
        i: item.i,
        w: item.w,
        h: item.h,
        x: item.x,
        y: item.y,
        isBounded: undefined,
        isDraggable: undefined,
        isResizable: undefined,
        maxH: undefined,
        maxW: undefined,
        minH: undefined,
        minW: undefined,
        resizeHandles: undefined,
        moved: undefined,
      }
      formatLayout.push(tmpObj)
    })
    setOriginalLayout(formatLayout)
    setLayout(formatLayout);

    let allSelectedMenu = cloneDeep(widgetSet);
    for (let key in widgetSet) {
      allSelectedMenu[key].check = true;
    }
    setWidgetSet(allSelectedMenu);
  }

  const onLayoutChange = (newLayout) => {
    const sortedLayout = newLayout.sort((a, b) => a.y - b.y || a.x - b.x);
    setLayout(sortedLayout);
    let hasFirst = hasFirstRender
    if (hasFirst) {
      setIfChange(true)
    }
    if (!hasFirst) {
      setHasFirstRender(true)
    }
  };

  useEffect(() => {
    if (Object.keys(statsData).length !== 0 && fetchDataMode === "fetchDashboardLayout") {
      if (checkMockup === 'false') {
        getWidgetData();
      } else {
        setShowMenu(true)
        setGridKey(prevKey => prevKey + 1);
        initLayout()
      }
    }
  }, [statsData, fetchDataMode]);

  useEffect(() => {
    const response = getData(widgetUuid);
    if (response && response.data) {
      setShowMenu(true)
      const blocks = response.data.blocks || [];
      rawBlocksRef.current = blocks;
      let formatLayout = makeLayoutData(blocks)

      setOriginalLayout(formatLayout)
      setGridKey(prevKey => prevKey + 1);
      setLayout(formatLayout)

      let selectedMenu = checkMenuSetting(formatLayout)
      setWidgetSet(selectedMenu)
      setFetchDataMode("intervalFetch")
    }
  }, [dataMap])

  useEffect(() => {
    if (Object.keys(statsData).length !== 0) {
      const accessPointsOverviewClientData = getPieChartData(statsData.accessPointsOverview.client, "apName", "clientCounts");
      setAccessPointsOverviewClientUsageData(accessPointsOverviewClientData);
      const accessPointsOverviewTrafficData = getPieChartData(statsData.accessPointsOverview.traffic, "apName", "usage", "GB");
      setAccessPointsOverviewTrafficUsageData(accessPointsOverviewTrafficData);

      const wirelessClientsRssiData = getPieChartData(statsData.wirelessClientsRssi, "rssi", "clientCounts");
      setWirelessClientsRssiUsageData(wirelessClientsRssiData);

      const wirelessClientsBandData = getPieChartData(statsData.wirelessClientsBand, "band", "clientCounts");
      setWirelessClientsBandUsageData(wirelessClientsBandData)

      const top5WirelessClientsbyTrafficData = getRankingListData(statsData.top5TrafficWirelessClients, "clientName", "usage", "GB");
      setTop5WirelessClientsbyTrafficUsageData(top5WirelessClientsbyTrafficData)

      const top5SwitchesByTrafficData = getRankingListData(statsData.top5TrafficSwitch, "name", "usage", "GB");
      setTop5SwitchesByTrafficUsageData(top5SwitchesByTrafficData)

      const top5AccessPointsByTrafficData = getRankingListData(statsData.top5TrafficAp, "name", "usage", "GB");
      setTop5AccessPointsByTrafficUsageData(top5AccessPointsByTrafficData)

      const wirelessTrafficData = statsData.wirelessTrafficUsage
      const seriesData = getLineChartData({ wirelessTrafficUsageTx: "Tx", wirelessTrafficUsageRx: "Rx" }, wirelessTrafficData, "MB");
      setWirelessTrafficUsageData(seriesData);
      const lastTx = bytesToKB(
        wirelessTrafficData?.wirelessTrafficUsageTx?.length
          ? wirelessTrafficData.wirelessTrafficUsageTx.at(-1).usage
          : 0
      );

      const lastRx = bytesToKB(
        wirelessTrafficData?.wirelessTrafficUsageRx?.length
          ? wirelessTrafficData.wirelessTrafficUsageRx.at(-1).usage
          : 0
      );

      setCurrentWirelessTrafficUsageData({ Tx: lastTx, Rx: lastRx })

      const top5SsidByTrafficData = getRankingListData(statsData.top5TrafficApSsid, "name", "usage", "GB");
      setTop5SsidByTrafficUsageData(top5SsidByTrafficData)

      const top5AccessPointsByWirelessClientData = getRankingListData(statsData.top5WirelessClientsAp, "apName", "clientCounts", "Client(s)");
      setTop5AccessPointsByWirelessClientUsageData(top5AccessPointsByWirelessClientData)

      const wirelessClientsActivitiesData = getBarChartData(
        statsData.wirelessClientsActivities,
        [
          {
            key: "new",
            name: "New",
            type: "main",
            isNegative: false,
          },
          {
            key: "existing",
            name: "Existing",
            type: "blueSecound",
            isNegative: false,
          },
          {
            key: "disconnected",
            name: "Disconnected",
            type: "greyThird",
            isNegative: true,
          },
        ]);
      setWirelessClientsActivitiesUsageData(wirelessClientsActivitiesData)
    }
  }, [statsData]);

  useEffect(() => {
    if (actionStatus.draggable) {
      setSortable(true)
      setAllowDrag(true)
    } else {
      setSortable(false)
      setAllowDrag(false)
      if (actionStatus.update.custom) {
        if (ifChange === true) {
          updateLayout();
          setIfChange(false)
        }
      } else if (actionStatus.update.system) {
        if (ifChange === true) {
          updateLayout();
          setIfChange(false)
        }
      } else {
        if (actionStatus.reset.custom) {
          if (ifChange === true) {
            let originData = cloneDeep(originalLayout)
            setLayout(originData);
            setIfChange(false)
          }
        } else if (actionStatus.reset.system) {
          initLayout();
        }
      }
    }
  }, [actionStatus]);

  useEffect(() => {
    if (targetWidget.check === true) {
      addItem(targetWidget.id)
    } else if (targetWidget.check === false) {
      removeItem(targetWidget.id)
    }
  }, [targetWidget])

  return (
    <>
      <div style={{ padding: '0px', }}>
        <ReactGridLayout
          key={gridKey}
          className="layout"
          layout={layout}
          cols={3}
          rowHeight={1}
          margin={[20, 10.5]}
          onLayoutChange={onLayoutChange}
          isResizable={false}
          isDraggable={allowDrag}
        >
          {layout.map((item) => (
            item.i === 'network-status' ? (
              <div key={item.i}>
                <NetworkStatus
                  sortable={sortable}
                  routerData={statsData.networkStatus.router}
                  switchData={statsData.networkStatus.switch}
                  apData={statsData.networkStatus.ap}
                  clientData={statsData.networkStatus.client}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'access-points-overview' ? (
              <div key={item.i}>
                <UsagePieChart
                  titleIcon={icn_bar_chart_horizontal}
                  title="Access Points Overview"
                  sortable={sortable}
                  data={accessPointsOverviewData}
                  handleLegendOnClick={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'wireless-clients-rssi' ? (
              <div key={item.i}>
                <UsagePieChart
                  titleIcon={icn_bar_chart_horizontal}
                  title="Wireless Clients by RSSI ≥ 70 dBm"
                  sortable={sortable}
                  data={rssiChartData}
                  pieChartFormattedLegendName={rssiChartLegend}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'wireless-clients-band' ? (
              <div key={item.i}>
                <UsagePieChart
                  titleIcon={icn_bar_chart_horizontal}
                  title="Wireless Clients by Band"
                  sortable={sortable}
                  data={bandChartData}
                  pieChartFormattedLegendName={bandChartLegend}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-wireless-clients-by-traffic' ? (
              <div key={item.i}>
                <UsageRankingList
                  titleIcon={icn_line_traffic}
                  title="Top 5 Wireless Clients by Traffic"
                  sortable={sortable}
                  data={top5WirelessClientsbyTrafficUsageData}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-switch-by-traffic' ? (
              <div key={item.i}>
                <UsageRankingList
                  titleIcon={icn_line_traffic}
                  title="Top 5 Switches by Traffic"
                  sortable={sortable}
                  data={top5SwitchesByTrafficUsageData}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-ap-by-traffic' ? (
              <div key={item.i}>
                <UsageRankingList
                  titleIcon={icn_line_traffic}
                  title="Top 5 Access Points by Traffic"
                  sortable={sortable}
                  data={top5AccessPointsByTrafficUsageData}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'wireless-traffic-usage' ? (
              <div key={item.i}>
                <UsageLineChart
                  sortable={sortable}
                  title="Access Point Wireless Traffic Usage"
                  data={wirelessTrafficUsageData}
                  lineChartFormattedLegendName={{
                    Tx: `Tx : ${currentWirelessTrafficUsageData.Tx} Kbps`,
                    Rx: `Rx : ${currentWirelessTrafficUsageData.Rx} Kbps`
                  }}
                  lineChartUnitData={{ tooltipUnit: "MB", timeRangeUnit: null }}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'ap-channel-utilization' ? (
              <div key={item.i}>
                <ApChannelUtilization
                  sortable={sortable}
                  data={statsData.apChannelUtilization}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-poe-power' ? (
              <div key={item.i}>
                <UsageRankingBarChart
                  title="Top 5 PoE Power Consumption"
                  sortable={sortable}
                  data={statsData.poeTop5}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-cpu' ? (
              <div key={item.i}>
                <UsageRankingBarChart
                  title="Top 5 CPU by Usage"
                  sortable={sortable}
                  data={statsData.cpuTop5}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-memory' ? (
              <div key={item.i}>
                <UsageRankingBarChart
                  title="Top 5 Memory by Usage"
                  sortable={sortable}
                  data={statsData.memTop5}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-ssid-by-traffic' ? (
              // Bug 33164 已說明目前不需要過濾不存在的 SSID
              // 統計過去資料為主 By Andy
              <div key={item.i}>
                <UsageRankingList
                  titleIcon={icn_bar_chart_horizontal}
                  title="Top 5 SSIDs by Traffic"
                  sortable={sortable}
                  data={top5SsidByTrafficUsageData}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'top-5-access-points-by-wireless-client' ? (
              <div key={item.i}>
                <UsageRankingList
                  titleIcon={icn_bar_chart_horizontal}
                  title="Top 5 Access Points by Wireless Client"
                  sortable={sortable}
                  data={top5AccessPointsByWirelessClientUsageData}
                  openDeviceDrawer={openDeviceDrawer}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'poe-power' ? (
              <div key={item.i}>
                <PoEPowerConsumption
                  sortable={sortable}
                  data={statsData.pocConsumption?.[0] || { budget: 0, consumption: 0 }}
                  style={{ display: "flex" }}
                />
              </div>
            ) : item.i === 'wireless-clients-activities' ? (
              <div key={item.i}>
                <UsageBarChart
                  title="Wireless Clients Activities"
                  sortable={sortable}
                  data={wirelessClientsActivitiesUsageData}
                  style={{ display: "flex" }}
                />
              </div>
            ) : (
              <></>
            )
          ))}
        </ReactGridLayout>
      </div>
    </>
  );
};

export default GridLayout;